// PlanetScene — the main R3F Canvas for the Personal Planet Observatory.
// Wires the real planet, atmosphere, starfield, scene landmarks,
// timeline path, camera rig, and lights into a single scene graph.

import {
  Suspense,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { useFrame } from '@react-three/fiber';

import type { Group } from 'three';

import { Planet } from './Planet';
import { Atmosphere } from './Atmosphere';
import { Clouds } from './Clouds';
import { Stars } from './Stars';
import { SceneLandmarks } from './SceneLandmarks';
import { SceneCameraRig } from './SceneCameraRig';
import { SceneColorGrade } from './SceneColorGrade';
import { TimelinePath } from './TimelinePath';
import { OrbitRings } from './OrbitRings';
import { Satellites } from './Satellites';
import { NoteFragments } from './NoteFragments';
import { SatelliteInteraction } from './SatelliteInteraction';
import { SurfaceVistas } from './SurfaceVistas';
import { SCENES, type SceneConfig } from './planetSections';
import { useWebGLAvailable } from '../three/useWebGLAvailable';
import { SceneLights } from '../three/SceneLights';
import { DevFpsBadge } from './DevFpsBadge';
import './PlanetScene.css';

// ---------------------------------------------------------------------------
// Performance tuning — DPR ladder + FPS targets.
//
// Target 50 FPS. If we fall below the threshold the DPR slides down a
// notch (1.5 → 1.25 → 1.0 → 0.75). When headroom returns the DPR climbs
// back. Bounds are tighter than drei's defaults (which only flip at
// ~50% deviation) so we react within a couple of sample windows.
// ---------------------------------------------------------------------------
const DPR_HIGH = 1.5;
const DPR_MID = 1.0;
const DPR_LOW = 0.75;
// PerformanceMonitor fires onDecline/onIncline when the average FPS
// strays past `bounds` (a function of `refreshrate`). The function
// returns [lower, upper] absolute FPS thresholds; falling below
// lower triggers decline, exceeding upper triggers incline. Tuned
// tight — around ±10 FPS around a 60 Hz refresh — so we react in a
// couple of sample windows instead of waiting for a huge deviation.
const DPR_BOUNDS = (refreshrate: number): [number, number] => [
  refreshrate * 0.65, // ≈ 39 FPS @ 60 Hz → decline
  refreshrate * 0.95, // ≈ 57 FPS @ 60 Hz → incline
];
// Step size for each decline / incline event.
const DPR_STEP = 0.25;

// ---------------------------------------------------------------------------
// Props / ref shape
// ---------------------------------------------------------------------------

export interface PlanetSceneHandle {
  /** The group containing the planet + landmarks + satellites.
   *  Used by the pointer drag hook to apply rotation deltas. */
  worldGroup: Group | null;
  /** The group whose children participate in raycasting. */
  interactiveGroup: Group | null;
  /** Read the current scene index. */
  getActiveSceneIndex: () => number;
}

export interface PlanetSceneProps {
  /** Fixed-pixel height; defaults to 100vh. The Canvas is `position: fixed`. */
  className?: string;
  /** Drag delta ref from usePlanetDrag. */
  dragDeltaRef?: React.MutableRefObject<number>;
  /** Is the user currently dragging? */
  isDraggingRef?: React.MutableRefObject<boolean>;
  /**
   * Fired once the R3F <Canvas onCreated> callback has run. The
   * LoadingSplash listens for this so it can fade out the moment the
   * 3D scene is actually live, not just mounted.
   */
  onCanvasReady?: () => void;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const PlanetScene = forwardRef<PlanetSceneHandle, PlanetSceneProps>(
  function PlanetScene(
    { className, dragDeltaRef, isDraggingRef, onCanvasReady },
    ref,
  ) {
    const hasWebGL = useWebGLAvailable();
    const worldGroupRef = useRef<Group | null>(null);
    const interactiveGroupRef = useRef<Group | null>(null);
    const [activeSceneIndex, setActiveSceneIndex] = useState(0);
    const [hoveredLandmarkKey, setHoveredLandmarkKey] = useState<string | null>(null);
    // Auto-detected device pixel ratio — lowered by PerformanceMonitor
    // when the FPS drops (see <PerformanceMonitor> below). Ladder:
    // 1.5 → 1.25 → 1.0 → 0.75. Step 0.25 keeps the visual shift
    // imperceptible at typical viewport sizes while still buying real
    // fill-rate back when the GPU is struggling.
    const [dpr, setDpr] = useState(DPR_HIGH);
    // True while a deep-dive panel is open — we drop to on-demand
    // rendering at a reduced frame target to save the GPU while the
    // user reads.
    const [panelOpen, setPanelOpen] = useState(false);
    // Current mode (poll for perf-related DPR override).
    const [mode, setMode] = useState<'narrative' | 'exploration'>(
      typeof window !== 'undefined' && window.__planetMode === 'exploration'
        ? 'exploration'
        : 'narrative',
    );

    // Poll the global panel flags (set by Satellites / NoteFragments /
    // DetailPanel). Cheap 200ms poll matches the rest of the HUD.
    useEffect(() => {
      if (typeof window === 'undefined') return;
      let last = false;
      const id = window.setInterval(() => {
        const open =
          !!window.__planetOpenPanel || !!window.__planetOpenPanelId;
        if (open !== last) {
          last = open;
          setPanelOpen(open);
        }
      }, 200);
      return () => window.clearInterval(id);
    }, []);

    // Poll the global mode so we can drop DPR on mobile when in
    // exploration mode (where the camera is constantly moving, GPU cost
    // goes up).
    useEffect(() => {
      if (typeof window === 'undefined') return;
      const id = window.setInterval(() => {
        const cur =
          window.__planetMode === 'exploration'
            ? 'exploration'
            : 'narrative';
        setMode((prev) => (prev === cur ? prev : cur));
      }, 200);
      return () => window.clearInterval(id);
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        get worldGroup() {
          return worldGroupRef.current;
        },
        get interactiveGroup() {
          return interactiveGroupRef.current;
        },
        getActiveSceneIndex: () => {
          if (typeof window === 'undefined') return 0;
          return window.__planetSceneIndex ?? 0;
        },
      }),
      [],
    );

    // Subscribe to scene-index changes so we can mark which landmark
    // is "active". The hook fires from useScrollTimeline.
    useState(() => {
      if (typeof window === 'undefined') return 0;
      // Best-effort initial sync.
      return window.__planetSceneIndex ?? 0;
    });

    // Use a layout effect to subscribe after mount.
    if (typeof window !== 'undefined') {
      // Cheap pub/sub: we just read window.__planetSceneIndex in a
      // microtask on mount. For the live updates, we lean on
      // <SubscribeScenes /> below.
    }

    const handleLandmarkClick = useCallback((scene: SceneConfig) => {
      if (typeof window === 'undefined') return;
      window.__planetOpenPanelId = scene.key;
      // Bump a global so the HUD layer can re-render and open the panel.
      window.dispatchEvent(new CustomEvent('planet:open-panel', { detail: scene.key }));
    }, []);

    if (!hasWebGL) {
      return <PlanetSceneFallback className={className} />;
    }

    return (
      <div
        className={['planet-scene', className].filter(Boolean).join(' ')}
        // The 3D scene is purely decorative — hide it from assistive
        // tech always, and explicitly mark the canvas as hidden again
        // when a detail panel is open so screen readers do not narrate
        // the WebGL output alongside the dialog.
        aria-hidden="true"
      >
        <Canvas
          // Exploration mode on mobile: clamp DPR to [1, 1.25] because
          // the camera is constantly moving (drag + auto-orbit) and
          // pixel-fill rate spikes. Desktop keeps the auto-tuned DPR.
          dpr={
            mode === 'exploration' && typeof window !== 'undefined' && window.innerWidth < 861
              ? [1, 1.25]
              : dpr
          }
          gl={{
            antialias: true,
            alpha: true,
            powerPreference: 'high-performance',
          }}
          camera={{ position: [0, 0.5, 8], fov: 42, near: 0.1, far: 100 }}
          // Exploration mode requires continuous rendering for smooth
          // orbit. The panel-open demand mode is preserved for narrative.
          frameloop={
            panelOpen
              ? 'demand'
              : mode === 'exploration'
                ? 'always'
                : 'always'
          }
          aria-hidden={panelOpen ? 'true' : undefined}
          onCreated={({ gl }) => {
            // Transparent clear — the CSS scene field (--scene-base,
            // one bold color per chapter) shows through behind the
            // planet, so each chapter reads as a full color block.
            gl.setClearColor('#0a0a0f', 0);
            // Notify parent that the WebGL context is live. The
            // LoadingSplash listens for this so it can fade out as
            // soon as R3F is actually rendering, not just mounted.
            onCanvasReady?.();
          }}
        >
          {/* Phase 17 — Drei's <PerformanceMonitor> had a useEffect
              that read samples.length before the ref was attached
              under our Suspense boundary (same class of bug as the
              earlier <Detailed> and <Html> crashes). We removed the
              dependency and now drive DPR from a custom FPS sampler
              below (CustomDprAdapter). The site keeps auto-degradation
              but stops crashing on canvas mount. */}
          <CustomDprAdapter
            onDprChange={setDpr}
            low={DPR_LOW}
            high={DPR_HIGH}
            step={DPR_STEP}
            onFps={(fps) => {
              if (typeof window !== 'undefined') {
                const w = window as Window & { __planetFps?: number };
                w.__planetFps = fps;
              }
            }}
          />
          {/* When a panel is open we render on demand at a reduced rate
              to save the GPU. This ticker keeps just enough frames alive
              for the ambient motion to breathe. */}
          <DemandTicker active={panelOpen} fps={30} />
          <Suspense fallback={null}>
            {/* 1. Lights — scene-global. Hemisphere + directional +
                ambient + the mint tracking point light. */}
            <SceneLights activeSceneIndex={activeSceneIndex} />

            {/* 2. Stars — infinite-distance background. NOT part of
                the world group (must not rotate with the planet). */}
            <Stars />

            {/* World group — planet + landmarks + satellites + rings
                rotate together. Drag deltas and scroll-driven
                planetRotation are applied here. */}
            <group ref={worldGroupRef} name="planet-world">
              {/* 3. Planet body + atmosphere at the center. */}
              <Planet
                dragDeltaRef={dragDeltaRef}
                isDraggingRef={isDraggingRef}
              />
              <Clouds />
              <Atmosphere />
              <SurfaceVistas activeSceneIndex={activeSceneIndex} />

              {/* 4. Scene landmarks anchored to the planet. */}
              <SceneLandmarks
                activeSceneIndex={activeSceneIndex}
                hoveredLandmarkKey={hoveredLandmarkKey}
                onLandmarkClick={handleLandmarkClick}
              />

              {/* 5. Timeline path — sits in world space, rotates with the planet. */}
              <TimelinePath
                activeSceneIndex={activeSceneIndex}
                isActive={activeSceneIndex === 5}
              />

              {/* 6. Orbit rings (decoration). */}
              <OrbitRings elevation={0.2} />

              {/* 7. Satellites — orbit the planet. */}
              <Satellites />

              {/* 8. Note fragments — drift around the planet. */}
              <NoteFragments />

              {/* Subgroup for the raycaster scope. SceneLandmarks
                  reads from this group via ref. */}
              <group ref={interactiveGroupRef} name="interactive-raycast" />
            </group>

            {/* 9. Interaction raycaster — runs at scene level so it
                sees both satellites and fragments. Sits outside the
                world group (no rotation dependency). */}
            <SatelliteInteraction enabled />

            {/* 10. Camera rig: reads window.__planetProgress every frame.
                MUST be last so it controls the camera over everything
                else. */}
            <SceneCameraRig
              isDraggingRef={isDraggingRef}
              dragDeltaRef={dragDeltaRef}
            />

            {/* Subscribe to scene changes — keeps the active index
                in sync with the scroll timeline. */}
            <SubscribeScenes
              onScene={(idx) => setActiveSceneIndex(idx)}
            />
          </Suspense>
        </Canvas>

        {/* Per-scene color grading + tint + boundary vignette. DOM
            overlays that sit above the canvas, below the HUD. */}
        <SceneColorGrade />

        {/* Dev-only FPS badge. Renders nothing in production
            (import.meta.env.DEV is statically false after build).
            Lives outside the Canvas so it can use a DOM update loop
            without re-rendering any R3F tree. */}
        <DevFpsBadge />
      </div>
    );
  },
);

// ---------------------------------------------------------------------------
// DemandTicker — when the scene is in on-demand frameloop (a panel is
// open), invalidate at a reduced FPS so ambient motion keeps breathing
// without paying the full 60fps GPU cost. No-op when inactive (the
// Canvas runs frameloop="always" then).
// ---------------------------------------------------------------------------

function DemandTicker({ active, fps }: { active: boolean; fps: number }) {
  const invalidate = useThree((s) => s.invalidate);
  useEffect(() => {
    if (!active) return;
    const interval = 1000 / fps;
    const id = window.setInterval(() => invalidate(), interval);
    // Kick one frame immediately so the switch to demand doesn't freeze.
    invalidate();
    return () => window.clearInterval(id);
  }, [active, fps, invalidate]);
  return null;
}

// ---------------------------------------------------------------------------
// CustomDprAdapter — replaces Drei's <PerformanceMonitor>, which had a
// useEffect that read samples.length before the ref was attached when
// mounted under our Suspense boundary. We do the same job with a
// useFrame + a rolling 60-sample window guarded against undefined refs.
// ---------------------------------------------------------------------------
interface CustomDprAdapterProps {
  onDprChange: (updater: (d: number) => number) => void;
  low: number;
  high: number;
  step: number;
  onFps?: (fps: number) => void;
}
function CustomDprAdapter({
  onDprChange,
  low,
  high,
  step,
  onFps,
}: CustomDprAdapterProps) {
  const frameCountRef = useRef(0);
  const elapsedRef = useRef(0);
  const lastFireAtRef = useRef(0);
  useFrame((_, delta) => {
    const now = performance.now();
    frameCountRef.current += 1;
    elapsedRef.current += Math.min(delta, 0.25);
    if (elapsedRef.current < 1) return;

    const fps = frameCountRef.current / elapsedRef.current;
    frameCountRef.current = 0;
    elapsedRef.current = 0;
    onFps?.(fps);

    if (now - lastFireAtRef.current < 1500) return; // throttle DPR decisions
    lastFireAtRef.current = now;
    if (fps < 50) {
      onDprChange((d) => Math.max(low, +(d - step).toFixed(2)));
    } else if (fps > 57) {
      onDprChange((d) => Math.min(high, +(d + step).toFixed(2)));
    }
  });
  return null;
}

// ---------------------------------------------------------------------------
// SubscribeScenes — bridges the global window.__planetSceneIndex to
// React state. Lives inside the Canvas so it can use useFrame if
// needed; for now a tiny effect is enough.
// ---------------------------------------------------------------------------

function SubscribeScenes({
  onScene,
}: {
  onScene: (idx: number) => void;
}) {
  // useState is a one-liner side effect we don't need here; the
  // subscription is just a render-bump.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, force] = useState(0);
  if (typeof window === 'undefined') return null;
  // Set up a one-shot subscription on first render via a layout-ish
  // effect. We can't use useEffect inside the Canvas without
  // violating the rules of hooks only if we conditionally call it,
  // but it's fine here.
  if (!(window as any).__planetSceneSubInstalled) {
    (window as any).__planetSceneSubInstalled = true;
    // Patch the existing listeners in useScrollTimeline by hooking
    // into the global ref. Simpler: poll on a 200ms interval.
    let last = -1;
    const id = window.setInterval(() => {
      const cur = window.__planetSceneIndex ?? 0;
      if (cur !== last) {
        last = cur;
        onScene(cur);
        force((n) => (n + 1) % 1024);
      }
    }, 200);
    // Cleanup is not necessary for a singleton app, but be tidy:
    (window as any).__planetSceneSubInterval = id;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Fallback for non-WebGL browsers. Renders a soft radial gradient via
// CSS, matching the dark mint palette so the page still feels alive.
// ---------------------------------------------------------------------------

function PlanetSceneFallback({ className }: { className?: string }) {
  return (
    <div
      className={['planet-scene', 'planet-scene--fallback', className]
        .filter(Boolean)
        .join(' ')}
      aria-hidden="true"
    />
  );
}

// Augment the global Window type with our scratch refs.
declare global {
  interface Window {
    __planetOpenPanelId?: string | null;
  }
}

// Re-export scene count for consumers that want to size the HUD.
export { SCENES };
